import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, MessageSquare } from 'lucide-react';
import { Badge } from '@/components/atoms/badge';
import { Button } from '@/components/atoms/button';
import { ConversationListItem } from '@/components/messaging';
import { useGetConversations, useUnreadCount } from '@/lib/api/messaging';

interface ConversationListContainerProps {
  onConversationSelect: (orderId: string) => void;
  selectedOrderId?: string;
}

type FilterStatus = 'all' | 'active' | 'disputed' | 'closed';

export function ConversationListContainer({
  onConversationSelect,
  selectedOrderId,
}: ConversationListContainerProps) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<FilterStatus>('all');

  const {
    data: conversationsData,
    isLoading,
    refetch,
  } = useGetConversations();

  const { data: unreadData } = useUnreadCount();

  const conversations = conversationsData?.conversations || [];

  const filteredConversations = useMemo(() => {
    if (filter === 'all') return conversations;
    if (filter === 'closed') {
      return conversations.filter((c) =>
        ['closed', 'resolved', 'expired'].includes(c.status)
      );
    }
    return conversations.filter((c) => c.status === filter);
  }, [conversations, filter]);

  const filters: { key: FilterStatus; label: string }[] = [
    { key: 'all', label: t('messaging.filter.all', 'Toutes') },
    { key: 'active', label: t('messaging.filter.active', 'Actives') },
    { key: 'disputed', label: t('messaging.filter.disputed', 'Litiges') },
    { key: 'closed', label: t('messaging.filter.closed', 'Fermées') },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="size-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between p-4 border-b">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold">
            {t('messaging.conversations', 'Conversations')}
          </h2>
          {unreadData && unreadData.unreadCount > 0 && (
            <Badge variant="default" className="text-xs">
              {unreadData.unreadCount > 99 ? '99+' : unreadData.unreadCount}
            </Badge>
          )}
        </div>
        <Button variant="ghost" size="sm" onClick={() => refetch()}>
          {t('common.refresh', 'Actualiser')}
        </Button>
      </div>

      <div className="flex gap-2 p-3 border-b bg-muted/30">
        {filters.map((f) => (
          <Button
            key={f.key}
            variant={filter === f.key ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(f.key)}
            className="text-xs"
          >
            {f.label}
          </Button>
        ))}
      </div>

      {filteredConversations.length === 0 ? (
        <div className="flex flex-col items-center justify-center flex-1 p-6 text-center">
          <MessageSquare className="size-12 text-muted-foreground mb-4" />
          <p className="text-sm text-muted-foreground">
            {t('messaging.noConversations', 'Aucune conversation')}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {t(
              'messaging.noConversationsHint',
              'Les conversations avec vos clients apparaîtront ici'
            )}
          </p>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto">
          {filteredConversations.map((conversation) => (
            <ConversationListItem
              key={conversation.id}
              conversation={conversation}
              onClick={() => onConversationSelect(conversation.order.orderId)}
              isSelected={selectedOrderId === conversation.order.orderId}
            />
          ))}
        </div>
      )}
    </div>
  );
}
